/**
 * React hook for raffle WebSocket connection
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  RaffleWebSocketClient,
  type ServerEvent,
  type ServerRaffleStateEvent,
  type ServerDrawStartEvent,
  type ServerDrawProgressEvent,
  type ServerDrawResultEvent,
  type ServerWinnerEvent,
  type ServerRaffleCompletedEvent,
  type ServerReactionBurstEvent,
  type ServerNextPrizeEvent,
  type ServerShowWinnerEvent,
  type ServerShowCompletedEvent,
  type ServerTestResetEvent,
  type RaffleState,
  type PrizeInfo,
  type DrawInfo,
  type WinnerInfo,
  type ReactionEmoji,
  type ReactionBurstItem,
} from "@/lib/websocket";

// =============================================================================
// Types
// =============================================================================

export interface RaffleSocketState {
  // Connection state
  isConnected: boolean;
  isJoined: boolean;
  error: string | null;

  // Raffle state
  raffle: RaffleState | null;
  currentPrize: PrizeInfo | null;
  prizes: Array<PrizeInfo & { isDrawn: boolean }>;
  ticketCount: number;
  completedDraws: DrawInfo[];
  activeRaffleId: string | null; // ID of active raffle for redirect

  // Animation state
  isDrawing: boolean;
  drawingPrize: PrizeInfo | null;
  currentTicketNumber: string | null;
  drawProgress: number;
  animationDuration: number;
  ticketNumbers: string[];

  // Result state
  lastWinner: WinnerInfo | null;
  lastDrawnPrize: PrizeInfo | null; // Prize that was just drawn (for transition display)
  showingWinner: boolean; // Roller stopped, waiting for admin to show winner
  showWinnerConfirmed: boolean; // Admin confirmed - show winner screen on broadcast
  isWinner: boolean; // Current user won
  wonPrize: PrizeInfo | null;

  // Completion
  isCompleted: boolean;
  showCompletedConfirmed: boolean; // Admin triggered final screen display

  // Reactions
  reactionBurst: ReactionBurstItem[];
  reactionTrigger: number;

  // Pending winner (waiting for roller animation to complete)
  pendingWinnerDraw: DrawInfo | null;
}

export interface UseRaffleSocketOptions {
  raffleId: string;
  accountId?: string;
  onWin?: (prize: PrizeInfo, ticketNumber: string) => void;
  onDrawStart?: (prize: PrizeInfo) => void;
  onDrawResult?: (winner: WinnerInfo, prize: PrizeInfo) => void;
  onShowWinner?: (winner: WinnerInfo, prize: PrizeInfo) => void;
  onNextPrize?: (prize: PrizeInfo | null) => void;
  onShowCompleted?: (draws: DrawInfo[]) => void;
  onComplete?: (draws: DrawInfo[]) => void;
  onReactionBurst?: (reactions: ReactionBurstItem[]) => void;
  onTestReset?: () => void;
}

export interface UseRaffleSocketReturn extends RaffleSocketState {
  connect: () => void;
  disconnect: () => void;
  resetWinnerState: () => void;
  sendReaction: (emoji: ReactionEmoji) => void;
  confirmAnimationComplete: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useRaffleSocket(
  options: UseRaffleSocketOptions
): UseRaffleSocketReturn {
  const { raffleId, accountId, onWin, onDrawStart, onDrawResult, onShowWinner, onNextPrize, onShowCompleted, onComplete, onReactionBurst, onTestReset } =
    options;

  // State
  const [state, setState] = useState<RaffleSocketState>({
    isConnected: false,
    isJoined: false,
    error: null,
    raffle: null,
    currentPrize: null,
    prizes: [],
    ticketCount: 0,
    completedDraws: [],
    activeRaffleId: null,
    isDrawing: false,
    drawingPrize: null,
    currentTicketNumber: null,
    drawProgress: 0,
    animationDuration: 0,
    ticketNumbers: [],
    lastWinner: null,
    lastDrawnPrize: null,
    showingWinner: false,
    showWinnerConfirmed: false,
    isWinner: false,
    wonPrize: null,
    isCompleted: false,
    showCompletedConfirmed: false,
    reactionBurst: [],
    reactionTrigger: 0,
    pendingWinnerDraw: null,
  });

  // Refs
  const clientRef = useRef<RaffleWebSocketClient | null>(null);
  const callbacksRef = useRef({ onWin, onDrawStart, onDrawResult, onShowWinner, onNextPrize, onShowCompleted, onComplete, onReactionBurst, onTestReset });

  // Update callbacks ref
  useEffect(() => {
    callbacksRef.current = { onWin, onDrawStart, onDrawResult, onShowWinner, onNextPrize, onShowCompleted, onComplete, onReactionBurst, onTestReset };
  }, [onWin, onDrawStart, onDrawResult, onShowWinner, onNextPrize, onShowCompleted, onComplete, onReactionBurst, onTestReset]);

  // Handle server events
  const handleMessage = useCallback(
    (event: ServerEvent) => {
      switch (event.type) {
        case "joined":
          setState((s) => ({ ...s, isJoined: true, error: null }));
          break;

        case "left":
          setState((s) => ({ ...s, isJoined: false }));
          break;

        case "error":
          setState((s) => ({ ...s, error: event.message }));
          break;

        case "raffle:state":
          handleRaffleState(event);
          break;

        case "raffle:draw_start":
          handleDrawStart(event);
          break;

        case "raffle:draw_progress":
          handleDrawProgress(event);
          break;

        case "raffle:draw_result":
          handleDrawResult(event);
          break;

        case "raffle:winner":
          handleWinner(event);
          break;

        case "raffle:completed":
          handleCompleted(event);
          break;

        case "raffle:reaction_burst":
          handleReactionBurst(event);
          break;

        case "raffle:next_prize":
          handleNextPrize(event);
          break;

        case "raffle:show_winner":
          handleShowWinner(event);
          break;

        case "raffle:show_completed":
          handleShowCompleted(event);
          break;

        case "raffle:test_reset":
          handleTestReset(event);
          break;
      }
    },
    []
  );

  const handleRaffleState = useCallback((event: ServerRaffleStateEvent) => {
    setState((s) => ({
      ...s,
      raffle: event.raffle,
      currentPrize: event.currentPrize,
      prizes: event.prizes,
      ticketCount: event.ticketCount,
      ticketNumbers: event.ticketNumbers, // Set ticket numbers from state
      completedDraws: event.completedDraws,
      isCompleted: event.raffle.status === "COMPLETED",
      activeRaffleId: event.activeRaffleId ?? null,
    }));
  }, []);

  const handleDrawStart = useCallback((event: ServerDrawStartEvent) => {
    setState((s) => ({
      ...s,
      isDrawing: true,
      drawingPrize: event.prize,
      currentTicketNumber: null,
      drawProgress: 0,
      animationDuration: event.durationMs,
      ticketNumbers: event.ticketNumbers,
      lastWinner: null,
    }));
    callbacksRef.current.onDrawStart?.(event.prize);
  }, []);

  const handleDrawProgress = useCallback((event: ServerDrawProgressEvent) => {
    setState((s) => ({
      ...s,
      currentTicketNumber: event.currentTicketNumber,
      drawProgress: event.progress,
    }));
  }, []);

  const handleDrawResult = useCallback((event: ServerDrawResultEvent) => {
    // Create pending draw to be added after roller animation completes
    const pendingDraw: DrawInfo = {
      prizeId: event.prizeId,
      prizeName: event.prize.name,
      prizeDescription: event.prize.description,
      ticketNumber: event.winner.ticketNumber,
      winnerName: event.winner.name,
      winnerUsername: event.winner.username,
    };

    setState((s) => ({
      ...s,
      isDrawing: false,
      drawProgress: 100,
      currentTicketNumber: event.winner.ticketNumber,
      lastWinner: event.winner,
      lastDrawnPrize: event.prize, // Save drawn prize for display during transition
      showingWinner: true, // Wait for admin to proceed to next prize
      // DON'T update currentPrize here - wait for raffle:next_prize event
      // Mark prize as drawn
      prizes: s.prizes.map((p) =>
        p.id === event.prizeId ? { ...p, isDrawn: true } : p
      ),
      // Store pending draw - will be added to completedDraws when roller animation completes
      pendingWinnerDraw: pendingDraw,
    }));
    callbacksRef.current.onDrawResult?.(event.winner, event.prize);
  }, []);

  const handleWinner = useCallback((event: ServerWinnerEvent) => {
    setState((s) => ({
      ...s,
      isWinner: true,
      wonPrize: event.prize,
    }));
    callbacksRef.current.onWin?.(event.prize, event.ticketNumber);
  }, []);

  const handleCompleted = useCallback((event: ServerRaffleCompletedEvent) => {
    setState((s) => ({
      ...s,
      isCompleted: true,
      raffle: s.raffle ? { ...s.raffle, status: "COMPLETED" } : null,
      // Show final screen automatically on completion
      showCompletedConfirmed: true,
      // Hide winner screen if it was shown
      showWinnerConfirmed: false,
      showingWinner: false,
      // Update draws from event
      completedDraws: event.draws,
    }));
    callbacksRef.current.onComplete?.(event.draws);
  }, []);

  const handleReactionBurst = useCallback((event: ServerReactionBurstEvent) => {
    setState((s) => ({
      ...s,
      reactionBurst: event.reactions,
      reactionTrigger: s.reactionTrigger + 1,
    }));
    callbacksRef.current.onReactionBurst?.(event.reactions);
  }, []);

  const handleNextPrize = useCallback((event: ServerNextPrizeEvent) => {
    setState((s) => {
      // If there's a pending winner that wasn't added yet, add it now before transitioning
      const updatedDraws = s.pendingWinnerDraw 
        ? [...s.completedDraws, s.pendingWinnerDraw]
        : s.completedDraws;
      
      return {
        ...s,
        // Update to next prize
        currentPrize: event.currentPrize,
        // Update ticket numbers (without won tickets)
        ticketNumbers: event.ticketNumbers,
        // Reset transition state
        showingWinner: false,
        showWinnerConfirmed: false,
        lastWinner: null,
        lastDrawnPrize: null,
        drawingPrize: null,
        // Reset roller state
        currentTicketNumber: null,
        drawProgress: 0,
        // Ensure pending winner is added and cleared
        completedDraws: updatedDraws,
        pendingWinnerDraw: null,
      };
    });
    callbacksRef.current.onNextPrize?.(event.currentPrize);
  }, []);

  const handleShowWinner = useCallback((event: ServerShowWinnerEvent) => {
    setState((s) => ({
      ...s,
      // Set confirmed winner display (admin triggered)
      showWinnerConfirmed: true,
      // Ensure winner info is available
      lastWinner: event.winner,
      lastDrawnPrize: event.prize,
    }));
    callbacksRef.current.onShowWinner?.(event.winner, event.prize);
  }, []);

  const handleShowCompleted = useCallback((event: ServerShowCompletedEvent) => {
    setState((s) => ({
      ...s,
      // Show final screen (admin triggered)
      showCompletedConfirmed: true,
      // Hide winner screen to show final screen instead
      showWinnerConfirmed: false,
      showingWinner: false,
      // Update completed draws from event
      completedDraws: event.draws,
    }));
    callbacksRef.current.onShowCompleted?.(event.draws);
  }, []);

  // Handle test raffle reset - reset all state to initial (READY)
  const handleTestReset = useCallback((_event: ServerTestResetEvent) => {
    setState((s) => ({
      ...s,
      // Reset raffle to READY state
      raffle: s.raffle ? { ...s.raffle, status: "READY" } : null,
      // Reset all prizes to undrawn
      prizes: s.prizes.map((p) => ({ ...p, isDrawn: false })),
      // Find first prize as current
      currentPrize: s.prizes.length > 0 ? s.prizes.sort((a, b) => a.sortOrder - b.sortOrder)[0] : null,
      // Clear all draws and winner state
      completedDraws: [],
      pendingWinnerDraw: null,
      isDrawing: false,
      drawingPrize: null,
      currentTicketNumber: null,
      drawProgress: 0,
      lastWinner: null,
      lastDrawnPrize: null,
      showingWinner: false,
      showWinnerConfirmed: false,
      isCompleted: false,
      showCompletedConfirmed: false,
    }));
    callbacksRef.current.onTestReset?.();
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
    }

    const client = new RaffleWebSocketClient({
      onConnect: () => {
        setState((s) => ({ ...s, isConnected: true, error: null }));
        client.joinRaffle(raffleId, accountId);
      },
      onDisconnect: () => {
        setState((s) => ({ ...s, isConnected: false, isJoined: false }));
      },
      onError: (error) => {
        setState((s) => ({ ...s, error: error.message }));
      },
      onMessage: handleMessage,
    });

    clientRef.current = client;
    client.connect();
  }, [raffleId, accountId, handleMessage]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
      clientRef.current = null;
    }
    setState((s) => ({
      ...s,
      isConnected: false,
      isJoined: false,
    }));
  }, []);

  // Reset winner state (after showing celebration)
  const resetWinnerState = useCallback(() => {
    setState((s) => ({
      ...s,
      isWinner: false,
      wonPrize: null,
    }));
  }, []);

  // Send reaction emoji
  const sendReaction = useCallback((emoji: ReactionEmoji) => {
    if (clientRef.current?.isConnected) {
      clientRef.current.sendReaction(raffleId, emoji);
    }
  }, [raffleId]);

  // Confirm roller animation is complete - move pending winner to completed draws
  const confirmAnimationComplete = useCallback(() => {
    setState((s) => {
      if (!s.pendingWinnerDraw) return s;
      return {
        ...s,
        completedDraws: [...s.completedDraws, s.pendingWinnerDraw],
        pendingWinnerDraw: null,
      };
    });
  }, []);

  // Auto-connect on mount, cleanup on unmount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Rejoin if raffleId or accountId changes
  useEffect(() => {
    if (clientRef.current?.isConnected) {
      clientRef.current.joinRaffle(raffleId, accountId);
    }
  }, [raffleId, accountId]);

  return {
    ...state,
    connect,
    disconnect,
    resetWinnerState,
    sendReaction,
    confirmAnimationComplete,
  };
}
